import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ConvexProvider } from "@/components/providers/ConvexProvider";
import { AuthAnalyticsProvider } from "@/components/providers/AuthAnalyticsProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GlobalErrorBoundary } from "@/components/GlobalErrorBoundary";
import { AppThemeEffect } from "@/components/AppThemeEffect";
import { TitleManager } from "@/components/TitleManager";

// Public Pages
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PricingPage from "./pages/PricingPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";

// Auth Components
import { LoginForm } from "@/components/auth/LoginForm";
import { SignUpForm } from "@/components/auth/SignUpForm";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
// App Components
import { GenniApp } from "@/components/GenniApp";
import { AdminDashboard } from "@/components/AdminDashboard";
import AdminDocs from "@/components/docs/AdminDocs";

// Subscription Pages
import SubscriptionSuccess from "./pages/SubscriptionSuccess";

// Other Pages
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <GlobalErrorBoundary>
    <ErrorBoundary>
      <ConvexProvider>
        <AuthAnalyticsProvider>
          <AppThemeEffect />
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <TitleManager />
              <ErrorBoundary>
                <Routes>
                  {/* Public Routes - No Authentication Required */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  {/* Authentication Routes */}
                  <Route path="/signin" element={<LoginForm />} />
                  <Route path="/login" element={<LoginForm />} />{" "}
                  {/* Legacy redirect */}
                  <Route path="/signup" element={<SignUpForm />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordForm />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordForm />}
                  />
                  {/* Protected App Routes */}
                  <Route
                    path="/app"
                    element={
                      <ErrorBoundary>
                        <ProtectedRoute>
                          <GenniApp />
                        </ProtectedRoute>
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/app/*"
                    element={
                      <ErrorBoundary>
                        <ProtectedRoute>
                          <GenniApp />
                        </ProtectedRoute>
                      </ErrorBoundary>
                    }
                  />
                  {/* Subscription Routes */}
                  <Route
                    path="/subscription/success"
                    element={
                      <ProtectedRoute>
                        <SubscriptionSuccess />
                      </ProtectedRoute>
                    }
                  />
                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <ErrorBoundary>
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/admin/docs/*"
                    element={
                      <ErrorBoundary>
                        <AdminRoute>
                          <AdminDocs />
                        </AdminRoute>
                      </ErrorBoundary>
                    }
                  />
                  {/* Catch-all route - must be last */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </ErrorBoundary>
            </BrowserRouter>
            </TooltipProvider>
          </QueryClientProvider>
        </AuthAnalyticsProvider>
      </ConvexProvider>
    </ErrorBoundary>
  </GlobalErrorBoundary>
);

export default App;
